import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MessageService } from '../../../services/message-service/message.service';
import { Tierreklamation } from '../classes/tierreklamation';
import { GruppeTier } from '../classes/gruppe-tier';
import { Subscription } from 'rxjs';
import { GruppeTierService } from '../gruppe-tier.service';
import { MatDialog } from '@angular/material/dialog';
import { GruppeTierReklamierenDialogComponent } from '../gruppe-tier-reklamieren-dialog/gruppe-tier-reklamieren-dialog.component';
import { GruppeTierViehhaendlerZuordnenDialogComponent } from './gruppe-tier-viehhaendler-zuordnen-dialog/gruppe-tier-viehhaendler-zuordnen-dialog.component';
import { GruppeTierBetriebskontaktHaendlerZuordnenDialogComponent } from './gruppe-tier-betriebskontakt-haendler-zuordnen-dialog/gruppe-tier-betriebskontakt-haendler-zuordnen-dialog.component';
import { BetriebskontakteService } from '../../stammdaten/betriebskontakte/betriebskontakte.service';
import { Betriebskontakt } from '../../stammdaten/betriebskontakte/betriebskontakt';
import { GruppeTierHerkunftBearbeitenDialogComponent } from './gruppe-tier-herkunft-bearbeiten-dialog/gruppe-tier-herkunft-bearbeiten-dialog.component';
import { PdfViewerInput } from '../../pdf-viewer/kiene-pdf-viewer/classes/pdf-viewer-input';
import { HttpParams } from '@angular/common/http';
import { StammdatenService } from '../../stammdaten/stammdaten.service';
import { LocalStorageService } from '../../../services/local-storage/local-storage.service';
@Component({
    selector: 'kiene-gruppe-tier-details',
    templateUrl: './gruppe-tier-details.component.html',
    styleUrls: ['./gruppe-tier-details.component.scss'],
})
export class GruppeTierDetailsComponent implements OnInit, OnDestroy {
    @Input('dialog') dialog = false;
    @Input('tier') tier: GruppeTier;
    @Output('changed') changed: EventEmitter<boolean> = new EventEmitter();

    noValue = '---';
    isLoading = true;
    reklamationen: Tierreklamation[];
    betriebskontaktHaendler: Betriebskontakt | null = null;
    legacyHaendlerAsBetriebskontakt: Betriebskontakt | null = null;
    legacyHaendlerData: any = null; // Echte Datenbank-Daten für Legacy Händler
    isLoadingLegacyHaendler = false;
    betriebskontaktData: any = null; // Echte Datenbank-Daten für Betriebskontakt
    isLoadingBetriebskontakt = false;

    tierSubscription: Subscription;
    pdfViewerInput: PdfViewerInput;

    constructor(
        private route: ActivatedRoute,
        private matDialog: MatDialog,
        private gruppeTierService: GruppeTierService,
        private messageService: MessageService,
        private betriebskontakteService: BetriebskontakteService,
        private stammdatenService: StammdatenService,
        private localStorageService: LocalStorageService,
    ) {}

    ngOnDestroy(): void {
        this.tierSubscription?.unsubscribe();
    }

    ngOnInit(): void {
        if (this.tier) {
            this.pdfViewerInput = new PdfViewerInput(
                'tier/read_or_mail_pdf_pass.php',
                new HttpParams().set('tier_id', this.tier?.tier_id),
                { betreff: `${this.tier?.gruppe} - Pass ${this.tier?.ohrmarke_notnull}` },
            );
            this.isLoading = false;
            this.loadBetriebskontaktHaendler();
            this.loadLegacyHaendlerData();
        } else {
            // read tierId from route
            this.route.params.subscribe(params => {
                if (params.gt_id) {
                    this.loadTier(params.gt_id);
                }
            });
        }
    }

    loadTier(tierId: string) {
        this.isLoading = true;
        this.tierSubscription?.unsubscribe();
        this.tierSubscription = this.gruppeTierService.ladeTier(Number(tierId)).subscribe({
            next: (tier: GruppeTier) => {
                this.isLoading = false;
                this.tier = tier;
                this.pdfViewerInput = new PdfViewerInput(
                    'tier/read_or_mail_pdf_pass.php',
                    new HttpParams().set('tier_id', this.tier?.tier_id),
                    { betreff: `${this.tier?.gruppe} - Pass ${this.tier?.ohrmarke_notnull}` },
                );
                this.loadBetriebskontaktHaendler();
                this.loadLegacyHaendlerData();
            },
            error: err => {
                this.isLoading = false;
                this.messageService.errorMessage('Fehler beim Laden des Tieres', err);
            },
        });
    }

    openTierreklamationHinzufuegen() {
        const dialogRef = this.matDialog.open(GruppeTierReklamierenDialogComponent, {
            width: '760px',
            maxWidth: '95vw',
            maxHeight: '85vh',
            autoFocus: false,
            data: { tier: this.tier },
        });

        dialogRef.afterClosed().subscribe(res => {
            if (res) {
                this.changed.emit(res);
            }
        });
    }

    openHerkunftBearbeiten() {
        const dialogRef = this.matDialog.open(GruppeTierHerkunftBearbeitenDialogComponent, {
            width: '800px',
            maxWidth: '95vw',
            maxHeight: '90vh',
            data: { tier: this.tier },
        });

        dialogRef.afterClosed().subscribe(res => {
            if (res) {
                this.changed.emit(res);
            }
        });
    }

    getIcon(status_id: number) {
        const id = Number(status_id);
        if (id === 142) {
            return 'check_circle';
        }
        if (id === 145) {
            return 'check_circle_outline';
        }
        if (id === 141 || id === 144) {
            return 'schedule';
        }
        if (id === 143) {
            return 'error';
        }
        return 'block'; // 140 und undefined etc.
    }

    getIconStatusClass(status_id: number) {
        const id = Number(status_id);
        if (id === 142 || id === 145) {
            return 'hit-success';
        }
        if (id === 141 || id === 144) {
            return 'hit-wait';
        }
        if (id === 143) {
            return 'hit-failed';
        }
        return 'hit-block'; // 140 und undefined etc.
    }

    hasHerkunftBearbeitenPermission(): boolean {
        return this.localStorageService.currentUserHasPermission(11014);
    }

    loadBetriebskontaktHaendler() {
        if (!this.tier?.betriebskontakt_haendler_id || !this.tier?.vvvo_betriebskontakt_haendler) {
            this.betriebskontaktHaendler = null;
            this.betriebskontaktData = null;
            return;
        }

        this.isLoadingBetriebskontakt = true;
        this.betriebskontakteService.sucheBetriebe(this.tier.vvvo_betriebskontakt_haendler).subscribe({
            next: response => {
                if (response.records && response.records.length > 0) {
                    // Finde den passenden Betrieb anhand der VVVO
                    const gefundenerBetrieb = response.records.find(
                        betrieb => betrieb.vvvo === this.tier.vvvo_betriebskontakt_haendler,
                    );

                    if (gefundenerBetrieb) {
                        this.betriebskontaktData = gefundenerBetrieb;
                        this.betriebskontaktHaendler = this.convertBetriebskontaktDataToBetriebskontakt();
                    } else {
                        // Fallback auf Tier-Daten wenn kein passender Betrieb gefunden
                        this.betriebskontaktData = null;
                        this.betriebskontaktHaendler = this.createFallbackBetriebskontakt();
                    }
                } else {
                    // Fallback auf Tier-Daten wenn keine Datenbank-Daten verfügbar
                    this.betriebskontaktData = null;
                    this.betriebskontaktHaendler = this.createFallbackBetriebskontakt();
                }
                this.isLoadingBetriebskontakt = false;
            },
            error: error => {
                console.error('Fehler beim Laden der Betriebskontakt-Daten:', error);
                this.messageService.errorMessage('Fehler beim Laden der Kontakt-Daten', error);
                // Fallback auf Tier-Daten bei Fehler
                this.betriebskontaktData = null;
                this.betriebskontaktHaendler = this.createFallbackBetriebskontakt();
                this.isLoadingBetriebskontakt = false;
            },
        });
    }

    /**
     * Erstellt ein Fallback-Betriebskontakt-Objekt aus den Tier-Daten
     */
    private createFallbackBetriebskontakt(): Betriebskontakt | null {
        if (!this.tier?.betriebskontakt_haendler_id || !this.tier?.betriebskontakt_haendler) {
            return null;
        }

        return {
            kunde_id: this.tier.betriebskontakt_haendler_id,
            bezeichnung: this.tier.betriebskontakt_haendler,
            bezeichnung_final: this.tier.betriebskontakt_haendler,
            vvvo: this.tier.vvvo_betriebskontakt_haendler || '',
            zusatz: '',
            zusatz_final: '',
            strasse: '',
            plz: '',
            ort: '',
            betriebsart_bezeichnung: '',
            kontakt_id: 0,
            benutzer_id: 0,
            favorit: 0,
            ist_kunde: 0,
            erstellt: new Date().toISOString(),
            erstellt_von: 0,
            editiert: new Date().toISOString(),
            editiert_von: 0,
        } as Betriebskontakt;
    }

    /**
     * Konvertiert echte Datenbank-Daten zu einem Betriebskontakt-Objekt
     */
    private convertBetriebskontaktDataToBetriebskontakt(): Betriebskontakt | null {
        if (!this.betriebskontaktData) {
            return null;
        }

        return {
            kontakt_id: this.betriebskontaktData.kontakt_id,
            benutzer_id: this.betriebskontaktData.benutzer_id,
            kunde_id: this.betriebskontaktData.kunde_id,
            favorit: this.betriebskontaktData.favorit,
            ist_kunde: this.betriebskontaktData.ist_kunde,
            bezeichnung: this.betriebskontaktData.bezeichnung,
            zusatz: this.betriebskontaktData.zusatz,
            vvvo: this.betriebskontaktData.vvvo,
            bezeichnung_final: this.betriebskontaktData.bezeichnung_final,
            zusatz_final: this.betriebskontaktData.zusatz_final,
            strasse: this.betriebskontaktData.strasse,
            plz: this.betriebskontaktData.plz,
            ort: this.betriebskontaktData.ort,
            ortsteil: this.betriebskontaktData.ortsteil,
            betriebsart_id: this.betriebskontaktData.betriebsart_id,
            betriebsart_bezeichnung: this.betriebskontaktData.betriebsart_bezeichnung,
            bereits_kontakt: this.betriebskontaktData.bereits_kontakt,
            telefon_1: this.betriebskontaktData.telefon_1,
            telefon_2: this.betriebskontaktData.telefon_2,
            mobil_1: this.betriebskontaktData.mobil_1,
            mobil_2: this.betriebskontaktData.mobil_2,
            email: this.betriebskontaktData.email,
            erstellt: this.betriebskontaktData.erstellt,
            erstellt_von: this.betriebskontaktData.erstellt_von,
            editiert: this.betriebskontaktData.editiert,
            editiert_von: this.betriebskontaktData.editiert_von,
        } as Betriebskontakt;
    }

    openBetriebskontaktHaendlerHinzufuegen() {
        const dialogRef = this.matDialog.open(GruppeTierBetriebskontaktHaendlerZuordnenDialogComponent, {
            width: '600px',
            maxWidth: '95vw',
            maxHeight: '85vh',
            data: { tier: this.tier },
            panelClass: 'kontakt-auswahl-dialog',
            disableClose: false,
            autoFocus: false,
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.tier = result;
                // Wenn ein Betriebskontakt zugeordnet wird und ein Viehhändler vorhanden ist, Viehhändler entfernen
                if (this.tier.betriebskontakt_haendler_id && this.tier.viehhaendler_id) {
                    this.removeViehhaendlerAndLoadBetriebskontakt();
                } else {
                    this.loadBetriebskontaktHaendler(); // Lade Betriebskontakt-Daten neu
                    this.changed.emit(true);
                }
            }
        });
    }

    removeBetriebskontaktHaendler() {
        // Verhindere mehrfache Aufrufe während ein Request läuft
        if (this.isLoading) {
            return;
        }

        if (this.tier?.betriebskontakt_haendler_id) {
            // Speichere die ursprüngliche ID für Rollback
            const originalId = this.tier.betriebskontakt_haendler_id;

            // Setze betriebskontakt_haendler_id auf null
            this.tier.betriebskontakt_haendler_id = null;

            this.isLoading = true;
            // Speichere die Änderung
            this.gruppeTierService.updateHaendler(this.tier).subscribe({
                next: () => {
                    this.isLoading = false;
                    this.betriebskontaktHaendler = null;
                    this.messageService.infoMessage('Händler erfolgreich entfernt!');
                    this.changed.emit(true);
                },
                error: err => {
                    this.isLoading = false;
                    this.messageService.errorMessage('Fehler beim Entfernen des Händlers!', err);
                    // Rollback: Setze die ursprüngliche ID zurück
                    this.tier.betriebskontakt_haendler_id = originalId;
                },
            });
        }
    }

    removeBetriebskontaktHaendlerAndLoadViehhaendler() {
        // Verhindere mehrfache Aufrufe während ein Request läuft
        if (this.isLoading) {
            return;
        }

        if (this.tier?.betriebskontakt_haendler_id) {
            // Speichere die ursprüngliche ID für Rollback
            const originalId = this.tier.betriebskontakt_haendler_id;

            // Setze betriebskontakt_haendler_id auf null
            this.tier.betriebskontakt_haendler_id = null;

            this.isLoading = true;
            // Speichere die Änderung
            this.gruppeTierService.updateHaendler(this.tier).subscribe({
                next: () => {
                    this.isLoading = false;
                    this.betriebskontaktHaendler = null;
                    this.messageService.infoMessage('Händler erfolgreich entfernt!');
                    this.loadLegacyHaendlerData(); // Lade Legacy Händler-Daten neu
                    this.changed.emit(true);
                },
                error: err => {
                    this.isLoading = false;
                    this.messageService.errorMessage('Fehler beim Entfernen des Händlers!', err);
                    // Rollback: Setze die ursprüngliche ID zurück
                    this.tier.betriebskontakt_haendler_id = originalId;
                },
            });
        }
    }

    openViehhaendlerHinzufuegen() {
        const dialogRef = this.matDialog.open(GruppeTierViehhaendlerZuordnenDialogComponent, {
            width: '500px',
            maxWidth: '90vw',
            data: { tier: this.tier },
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.tier = result;
                // Wenn ein Viehhändler zugeordnet wird und ein Betriebskontakt vorhanden ist, Betriebskontakt entfernen
                if (this.tier.viehhaendler_id && this.tier.betriebskontakt_haendler_id) {
                    this.removeBetriebskontaktHaendlerAndLoadViehhaendler();
                } else {
                    this.loadLegacyHaendlerData(); // Lade Legacy Händler-Daten neu
                    this.changed.emit(true);
                }
            }
        });
    }

    removeViehhaendler() {
        // Verhindere mehrfache Aufrufe während ein Request läuft
        if (this.isLoading) {
            return;
        }

        if (this.tier?.viehhaendler_id) {
            // Speichere die ursprüngliche ID für Rollback
            const originalId = this.tier.viehhaendler_id;

            // Setze viehhaendler_id auf null
            this.tier.viehhaendler_id = null;

            this.isLoading = true;
            // Speichere die Änderung
            this.gruppeTierService.updateHaendler(this.tier).subscribe({
                next: () => {
                    this.isLoading = false;
                    this.messageService.infoMessage('Händler erfolgreich entfernt!');
                    this.updateLegacyHaendlerCard(); // Aktualisiere die Card
                    this.changed.emit(true);
                },
                error: err => {
                    this.isLoading = false;
                    this.messageService.errorMessage('Fehler beim Entfernen des Händlers!', err);
                    // Rollback: Setze die ursprüngliche ID zurück
                    this.tier.viehhaendler_id = originalId;
                },
            });
        }
    }

    removeViehhaendlerAndLoadBetriebskontakt() {
        // Verhindere mehrfache Aufrufe während ein Request läuft
        if (this.isLoading) {
            return;
        }

        if (this.tier?.viehhaendler_id) {
            // Speichere die ursprüngliche ID für Rollback
            const originalId = this.tier.viehhaendler_id;

            // Setze viehhaendler_id auf null
            this.tier.viehhaendler_id = null;

            this.isLoading = true;
            // Speichere die Änderung
            this.gruppeTierService.updateHaendler(this.tier).subscribe({
                next: () => {
                    this.isLoading = false;
                    this.messageService.infoMessage('Händler erfolgreich entfernt!');
                    this.updateLegacyHaendlerCard(); // Aktualisiere die Card
                    this.loadBetriebskontaktHaendler(); // Lade Betriebskontakt-Daten neu
                    this.changed.emit(true);
                },
                error: err => {
                    this.isLoading = false;
                    this.messageService.errorMessage('Fehler beim Entfernen des Händlers!', err);
                    // Rollback: Setze die ursprüngliche ID zurück
                    this.tier.viehhaendler_id = originalId;
                },
            });
        }
    }

    /**
     * Konvertiert einen Legacy Händler in ein Betriebskontakt-Format für die Card-Darstellung
     */
    private convertLegacyHaendlerToBetriebskontakt(): Betriebskontakt | null {
        if (!this.tier?.viehhaendler_id) {
            return null;
        }

        // Verwende echte Datenbank-Daten falls verfügbar, sonst Fallback auf Tier-Daten
        const haendlerName = this.legacyHaendlerData?.name || this.tier.viehhaendler || 'Unbekannter Händler';
        const haendlerId = this.tier.viehhaendler_id;

        return {
            kontakt_id: haendlerId,
            benutzer_id: 0, // Legacy Händler haben keine benutzer_id
            kunde_id: haendlerId,
            favorit: this.legacyHaendlerData?.favorit || 0,
            ist_kunde: 1,
            bezeichnung: haendlerName,
            zusatz: '', // Kein Zusatz für Legacy Händler
            vvvo: this.legacyHaendlerData?.vvvo || '', // VVVO aus Datenbank
            bezeichnung_final: haendlerName,
            zusatz_final: '', // Kein Zusatz für Legacy Händler
            betriebsart_id: undefined, // Keine Betriebsart für Legacy Händler
            betriebsart_bezeichnung: undefined, // Keine Badge für Legacy Händler
            bereits_kontakt: 0,
            // Echte Datenbank-Daten für erweiterte Anzeige
            telefon_1: this.legacyHaendlerData?.telefon || '',
            telefon_2: this.legacyHaendlerData?.fax || '', // Fax als zweite Telefonnummer
            mobil_1: this.legacyHaendlerData?.mobil || '',
            mobil_2: '',
            email: this.legacyHaendlerData?.email || '',
            strasse: this.legacyHaendlerData?.strasse || '',
            plz: this.legacyHaendlerData?.plz || '',
            ort: this.legacyHaendlerData?.ort || '',
            ortsteil: '',
        };
    }

    /**
     * Aktualisiert die Legacy Händler Card bei Änderungen
     */
    private updateLegacyHaendlerCard(): void {
        this.legacyHaendlerAsBetriebskontakt = this.convertLegacyHaendlerToBetriebskontakt();
    }

    /**
     * Lädt die Legacy Händler-Daten aus der Datenbank
     */
    private loadLegacyHaendlerData(): void {
        if (!this.tier?.viehhaendler_id) {
            this.legacyHaendlerData = null;
            this.legacyHaendlerAsBetriebskontakt = null;
            return;
        }

        this.isLoadingLegacyHaendler = true;
        this.stammdatenService.getHaendler(this.tier.viehhaendler_id).subscribe({
            next: haendlerData => {
                this.legacyHaendlerData = haendlerData;
                this.legacyHaendlerAsBetriebskontakt = this.convertLegacyHaendlerToBetriebskontakt();
                this.isLoadingLegacyHaendler = false;
            },
            error: error => {
                console.error('Fehler beim Laden der Legacy Händler-Daten:', error);
                this.messageService.errorMessage('Fehler beim Laden der Händler-Daten', error);
                this.legacyHaendlerData = null;
                this.legacyHaendlerAsBetriebskontakt = null;
                this.isLoadingLegacyHaendler = false;
            },
        });
    }
}
